/** FELVORA — Inmobiliaria F7 A3 · Comisiones de asesores (devengada → autorizada → pagada). */
import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Coins, CheckCircle2, DollarSign, XCircle, User } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const mxn = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);

const badgeEstado = (e) => {
  if (e === "devengada") return "bg-slate-100 text-slate-700";
  if (e === "autorizada") return "bg-sky-100 text-sky-700";
  if (e === "pagada") return "bg-emerald-100 text-emerald-700";
  if (e === "cancelada") return "bg-rose-100 text-rose-700";
  return "bg-slate-100 text-slate-700";
};

export default function Comisiones() {
  const [items, setItems] = useState([]);
  const [asesores, setAsesores] = useState([]);
  const [filtroAsesor, setFiltroAsesor] = useState("todos");
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const [openAutorizar, setOpenAutorizar] = useState(false);
  const [openPagar, setOpenPagar] = useState(false);
  const [openCancelar, setOpenCancelar] = useState(false);
  const [sel, setSel] = useState(null);
  const [notas, setNotas] = useState("");
  const [motivo, setMotivo] = useState("");
  const [pagoData, setPagoData] = useState({
    fecha_pago: new Date().toISOString().slice(0, 10),
    referencia: "", notas: "", forma_pago: "transferencia",
  });
  const [busy, setBusy] = useState(false);

  const cargar = () => {
    const p = {};
    if (filtroAsesor !== "todos") p.asesor_user_id = filtroAsesor;
    if (filtroEstado !== "todos") p.estado = filtroEstado;
    api.get("/inmobiliaria/comisiones", { params: p }).then((r) => setItems(r.data)).catch(() => {});
  };
  useEffect(() => { cargar(); }, [filtroAsesor, filtroEstado]);
  useEffect(() => {
    api.get("/inmobiliaria/asesores").then((r) => setAsesores(r.data)).catch(() => {});
  }, []);

  const nombreAsesor = (id) => {
    const a = asesores.find((x) => x.id === id);
    return a?.name || (id ? id.slice(0, 8) : "—");
  };

  const totales = useMemo(() => {
    const t = { devengada: 0, autorizada: 0, pagada: 0, cancelada: 0 };
    items.forEach((c) => {
      const est = c.estado || "devengada";
      if (est in t) t[est] += c.monto_devengado || 0;
    });
    return t;
  }, [items]);

  const abrirAutorizar = (c) => { setSel(c); setNotas(""); setOpenAutorizar(true); };
  const confirmarAutorizar = async () => {
    setBusy(true);
    try {
      await api.post(`/inmobiliaria/comisiones/${sel.id}/autorizar`, { notas });
      toast.success("Comisión autorizada");
      setOpenAutorizar(false); cargar();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const abrirPagar = (c) => {
    setSel(c);
    setPagoData({
      fecha_pago: new Date().toISOString().slice(0, 10),
      referencia: "", notas: "", forma_pago: "transferencia",
    });
    setOpenPagar(true);
  };
  const confirmarPagar = async () => {
    setBusy(true);
    try {
      await api.post(`/inmobiliaria/comisiones/${sel.id}/pagar`, pagoData);
      toast.success("Pago registrado (documental — sin transferencia real)");
      setOpenPagar(false); cargar();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const abrirCancelar = (c) => { setSel(c); setMotivo(""); setOpenCancelar(true); };
  const confirmarCancelar = async () => {
    if (motivo.length < 4) return toast.error("Motivo requerido (>=4 caracteres)");
    setBusy(true);
    try {
      await api.post(`/inmobiliaria/comisiones/${sel.id}/cancelar`, { motivo });
      toast.success("Comisión cancelada");
      setOpenCancelar(false); cargar();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="comisiones-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-slate-900">
          <Coins className="mr-2 inline h-6 w-6 text-amber-700" />
          Comisiones de asesores
        </h1>
        <p className="text-sm text-slate-500">
          Ciclo: <b>devengada</b> → autorizada (dueño/coordinador) → <b>pagada</b> (registro documental — sin motor de pago real).
        </p>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="grid grid-cols-1 gap-3 p-4 md:grid-cols-3">
          <div>
            <label className="text-xs text-slate-500">Asesor</label>
            <Select value={filtroAsesor} onValueChange={setFiltroAsesor}>
              <SelectTrigger data-testid="select-filtro-asesor"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                {asesores.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Estado</label>
            <Select value={filtroEstado} onValueChange={setFiltroEstado}>
              <SelectTrigger data-testid="select-filtro-estado"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="devengada">Devengada</SelectItem>
                <SelectItem value="autorizada">Autorizada</SelectItem>
                <SelectItem value="pagada">Pagada</SelectItem>
                <SelectItem value="cancelada">Cancelada</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button variant="outline" onClick={cargar} className="w-full">Refrescar</Button>
          </div>
        </CardContent>
      </Card>

      {/* Totales */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Card><CardContent className="p-4"><div className="text-xs text-slate-500">Devengada</div><div className="text-xl font-semibold text-slate-700" data-testid="tot-devengada">{mxn(totales.devengada)}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-slate-500">Autorizada</div><div className="text-xl font-semibold text-sky-700" data-testid="tot-autorizada">{mxn(totales.autorizada)}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-slate-500">Pagada</div><div className="text-xl font-semibold text-emerald-700" data-testid="tot-pagada">{mxn(totales.pagada)}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-slate-500">Cancelada</div><div className="text-xl font-semibold text-rose-700" data-testid="tot-cancelada">{mxn(totales.cancelada)}</div></CardContent></Card>
      </div>

      {/* Kardex tabla */}
      <Card>
        <CardHeader><CardTitle className="text-sm">Kardex · {items.length} comisiones</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="tabla-comisiones">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="p-2">Asesor</th>
                <th className="p-2">Concepto</th>
                <th className="p-2">Origen</th>
                <th className="p-2 text-right">Base</th>
                <th className="p-2 text-right">%</th>
                <th className="p-2 text-right">Monto</th>
                <th className="p-2">Estado</th>
                <th className="p-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan="8" className="p-4 text-center text-slate-500">Sin comisiones registradas.</td></tr>
              )}
              {items.map((c) => (
                <tr key={c.id} className="border-t hover:bg-slate-50" data-testid={`row-com-${c.id}`}>
                  <td className="p-2 font-medium"><User className="mr-1 inline h-3 w-3 text-slate-400" />{nombreAsesor(c.asesor_user_id)}</td>
                  <td className="p-2 text-slate-700">{c.concepto || "—"}</td>
                  <td className="p-2 text-xs text-slate-500">{c.origen}</td>
                  <td className="p-2 text-right">{mxn(c.base_calculo)}</td>
                  <td className="p-2 text-right">{c.porcentaje}%</td>
                  <td className="p-2 text-right font-semibold">{mxn(c.monto_devengado)}</td>
                  <td className="p-2"><Badge className={badgeEstado(c.estado)} data-testid={`badge-com-${c.id}`}>{c.estado}</Badge></td>
                  <td className="p-2 space-x-1">
                    {c.estado === "devengada" && (
                      <>
                        <Button size="sm" onClick={() => abrirAutorizar(c)} data-testid={`btn-autorizar-${c.id}`}><CheckCircle2 className="mr-1 h-3 w-3" /> Autorizar</Button>
                        <Button size="sm" variant="outline" onClick={() => abrirCancelar(c)} data-testid={`btn-cancelar-${c.id}`}><XCircle className="mr-1 h-3 w-3" /> Cancelar</Button>
                      </>
                    )}
                    {c.estado === "autorizada" && (
                      <>
                        <Button size="sm" onClick={() => abrirPagar(c)} data-testid={`btn-pagar-${c.id}`}><DollarSign className="mr-1 h-3 w-3" /> Pagar</Button>
                        <Button size="sm" variant="outline" onClick={() => abrirCancelar(c)} data-testid={`btn-cancelar-${c.id}`}><XCircle className="mr-1 h-3 w-3" /></Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Modales */}
      <Dialog open={openAutorizar} onOpenChange={setOpenAutorizar}>
        <DialogContent data-testid="dialog-autorizar">
          <DialogHeader><DialogTitle>Autorizar comisión</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded bg-sky-50 p-2 text-sm text-sky-800">
              {sel && (<>
                Asesor: <b>{nombreAsesor(sel.asesor_user_id)}</b> · Monto: <b>{mxn(sel.monto_devengado)}</b>
              </>)}
            </div>
            <div>
              <label className="text-xs text-slate-500">Notas (opcional)</label>
              <Textarea value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Comentarios internos" data-testid="input-notas-autorizar" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenAutorizar(false)}>Cancelar</Button>
            <Button onClick={confirmarAutorizar} disabled={busy} data-testid="btn-confirmar-autorizar">Autorizar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openPagar} onOpenChange={setOpenPagar}>
        <DialogContent data-testid="dialog-pagar">
          <DialogHeader><DialogTitle>Registrar pago documental</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded bg-emerald-50 p-2 text-sm text-emerald-800">
              {sel && (<>Monto: <b>{mxn(sel.monto_devengado)}</b> · Asesor: <b>{nombreAsesor(sel.asesor_user_id)}</b></>)}
            </div>
            <div className="rounded bg-amber-50 p-2 text-xs text-amber-800">
              Registro documental de pago. Emergent no procesa dinero para comisiones (R4).
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-slate-500">Fecha del pago</label>
                <Input type="date" value={pagoData.fecha_pago} onChange={(e) => setPagoData({ ...pagoData, fecha_pago: e.target.value })} data-testid="input-fecha-pago" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Forma de pago</label>
                <Select value={pagoData.forma_pago} onValueChange={(v) => setPagoData({ ...pagoData, forma_pago: v })}>
                  <SelectTrigger data-testid="select-forma-pago"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="transferencia">Transferencia</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                    <SelectItem value="otro">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-500">Referencia bancaria</label>
              <Input value={pagoData.referencia} onChange={(e) => setPagoData({ ...pagoData, referencia: e.target.value })} placeholder="Folio SPEI, N.º cheque, etc." data-testid="input-referencia" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Notas</label>
              <Textarea value={pagoData.notas} onChange={(e) => setPagoData({ ...pagoData, notas: e.target.value })} data-testid="input-notas-pago" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenPagar(false)}>Cancelar</Button>
            <Button onClick={confirmarPagar} disabled={busy} data-testid="btn-confirmar-pagar">Registrar pago</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openCancelar} onOpenChange={setOpenCancelar}>
        <DialogContent data-testid="dialog-cancelar">
          <DialogHeader><DialogTitle>Cancelar comisión</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {sel && (<div className="rounded bg-rose-50 p-2 text-sm text-rose-800">
              Se cancelará la comisión de <b>{mxn(sel.monto_devengado)}</b> (estado {sel.estado}).
            </div>)}
            <div>
              <label className="text-xs text-slate-500">Motivo (obligatorio, ≥4 chars)</label>
              <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid="input-motivo-cancelar" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCancelar(false)}>Volver</Button>
            <Button onClick={confirmarCancelar} disabled={busy} className="bg-rose-700 hover:bg-rose-800" data-testid="btn-confirmar-cancelar">Cancelar comisión</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
